'use client';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import Link from 'next/link';
import { Menu, X } from 'lucide-react';
import { Logo } from '../logo';

/**
 * Sections that now live on the homepage link to their anchor; each section
 * carries its own link through to the fuller page. Resources has no homepage
 * section, so it stays a straight page link.
 */
const navLinks = [
  { label: 'Waitlist', href: '#waitlist', primary: true },
  { label: 'About', href: '#about' },
  { label: 'Testimonials', href: '#testimonials' },
  { label: 'Resources', href: '/public/resources' },
  { label: 'Pricing', href: '#membership' },
];

export function LandingNav() {
  const router = useRouter();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-[#cdc4c1]/95 backdrop-blur-sm">
      <div className="px-5 md:px-10 lg:px-22 4xl:px-50">
        <nav className="flex max-w-full items-center justify-between overflow-hidden py-4 pr-8">
          {/* Logo - Left. A real link rather than a div with onClick, so it's
              reachable by keyboard and announced with a name. */}
          <Link
            href="/"
            aria-label="Wee Amici — home"
            className="flex-shrink-0 cursor-pointer"
          >
            <Logo
              showDescription={false}
              className="h-16 w-auto md:h-20"
              aria-hidden="true"
            />
          </Link>

          {/* Nav Links - Center (hidden below lg) */}
          <div className="font-cinzel mx-4 hidden items-center gap-x-4 pr-8 lg:flex xl:gap-x-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className={
                  link.primary
                    ? 'rounded-full bg-[#786D67] px-4 py-1 text-[16px] font-bold whitespace-nowrap text-white uppercase shadow-sm transition-colors hover:bg-[#5F5751] xl:px-5 xl:text-[21px]'
                    : 'text-[15px] font-semibold whitespace-nowrap text-black uppercase transition-colors hover:text-[#554E49] xl:text-[20px]'
                }
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Login / Sign Up - Right (hidden below lg) */}
          <div className="hidden flex-shrink-0 items-center gap-2 lg:flex xl:gap-3">
            <button
              onClick={() => router.push('/login')}
              className="rounded-full bg-[#eae7e0] px-4 py-1.5 text-[14px] font-bold whitespace-nowrap text-black transition-colors hover:bg-[#ddd8d2] xl:px-5 xl:text-[15px]"
            >
              Login
            </button>
            <button
              onClick={() => router.push('/signup')}
              className="rounded-full bg-[#786D67] px-4 py-1.5 text-[14px] font-bold whitespace-nowrap text-white transition-colors hover:bg-[#5F5751] xl:px-5 xl:text-[15px]"
            >
              Sign Up
            </button>
          </div>

          {/* Hamburger menu (shown below lg) */}
          <button
            className="p-2 lg:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div className="mb-4 space-y-3 rounded-xl bg-[#eae7e0] px-5 py-4 lg:hidden">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className={
                  link.primary
                    ? 'font-cinzel inline-block rounded-full bg-[#786D67] px-4 py-1.5 text-[17px] font-bold text-white uppercase transition-colors hover:bg-[#5F5751]'
                    : 'font-cinzel block text-[16px] font-semibold text-black uppercase transition-colors hover:text-[#554E49]'
                }
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <div className="flex gap-3 border-t border-[#cdc4c1] pt-2">
              <button
                onClick={() => {
                  router.push('/login');
                  setMobileMenuOpen(false);
                }}
                className="font-cinzel flex-1 rounded-full bg-[#cdc4c1] py-2 text-[15px] font-bold text-black transition-colors hover:bg-[#b8b0ad]"
              >
                Login
              </button>
              <button
                onClick={() => {
                  router.push('/signup');
                  setMobileMenuOpen(false);
                }}
                className="font-cinzel flex-1 rounded-full bg-[#786D67] py-2 text-[15px] font-bold text-white transition-colors hover:bg-[#5F5751]"
              >
                Sign Up
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
